import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, cn, inr, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button, Card, Chip, Input, Select, Skeleton } from "../components/ui";
import {
  Users, DollarSign, TrendingUp, Building2, RotateCcw, X, AlertCircle,
  CheckCircle, BarChart2, Search, ChevronRight,
} from "lucide-react";

const PAGE_SIZE = 20;

// Laid out for the app's phone-width column (max 480px), so no sm:/lg: breakpoints.
export default function AdminDashboardPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState({ users: [], total: 0, page: 1 });
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const usersRef = useRef(null);

  const fetchAnalytics = async () => {
    try {
      const { data: res } = await api.get("/admin/analytics");
      setData(res);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (page = 1) => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: String(PAGE_SIZE) });
      if (searchQuery) params.append("search", searchQuery);
      if (planFilter) params.append("plan", planFilter);
      if (statusFilter) params.append("status", statusFilter);
      const { data: res } = await api.get(`/admin/users?${params.toString()}`);
      setUsers(res);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_superadmin) fetchAnalytics();
    else nav("/home", { replace: true });
  }, [user, nav]);

  // Refetch users once typing pauses or a filter changes (uses the latest values, not stale state)
  useEffect(() => {
    if (!user?.is_superadmin) return;
    const t = setTimeout(() => fetchUsers(1), 300);
    return () => clearTimeout(t);
  }, [searchQuery, planFilter, statusFilter, user]);

  if (loading) {
    return (
      <div className="pb-24 px-5 pt-6 space-y-3">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" />
        </div>
        <Skeleton className="h-52" />
      </div>
    );
  }

  return (
    <div className="pb-24">
      <div className="px-5 pt-6 pb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold">Platform</div>
          <h1 className="text-2xl font-bold text-ink leading-tight">Admin Dashboard</h1>
        </div>
        <button
          onClick={() => { setLoading(true); fetchAnalytics(); }}
          className="h-10 w-10 shrink-0 rounded-2xl bg-black/5 grid place-items-center btn-press"
          aria-label="Refresh analytics"
          data-testid="refresh-analytics-btn"
        >
          <RotateCcw size={17} />
        </button>
      </div>

      <div className="px-5 space-y-4">
        {/* Key metrics */}
        <div className="grid grid-cols-2 gap-3">
          <MetricCard title="Subscribers" value={data?.active_subscribers || 0} icon={Users} color="brand"
            subtitle={`${data?.paid_users || 0} paid • ${data?.free_users || 0} free`} />
          <MetricCard title="MRR" value={inr(data?.mrr || 0)} icon={DollarSign} color="green"
            subtitle={`ARR ${inr(data?.arr || 0)}`} />
          <MetricCard title="Clubs" value={data?.total_clubs || 0} icon={Building2} color="blue"
            subtitle={`+${data?.clubs_this_month || 0} this month`} onClick={() => nav("/admin/clubs")} />
          <MetricCard title="Trial conv." value={`${data?.trial_conversion_rate || 0}%`} icon={TrendingUp} color="amber"
            subtitle={`${data?.failed_payments_this_month || 0} failed payments`} />
        </div>

        {/* Shortcuts */}
        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" size="sm" className="h-11" onClick={() => nav("/admin/clubs")} data-testid="all-clubs-btn">
            <Building2 size={16} /> All clubs
          </Button>
          <Button variant="outline" size="sm" className="h-11"
            onClick={() => usersRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            data-testid="manage-users-btn">
            <Users size={16} /> Users
          </Button>
        </div>

        {/* Revenue */}
        <Card className="p-4">
          <SectionHeading icon={BarChart2} iconClass="text-brand">Revenue • last 12 months</SectionHeading>
          <RevenueChart revenue={data?.revenue_by_month || {}} />
        </Card>

        {/* Churn & renewals */}
        <Card className="p-4">
          <SectionHeading icon={RotateCcw} iconClass="text-red-500">This month</SectionHeading>
          <div className="divide-y divide-black/[0.06]">
            <StatRow label="Renewals" value={data?.renewals_this_month || 0} icon={CheckCircle} color="green" />
            <StatRow label="Churned" value={data?.churned_this_month || 0} icon={X} color="red" />
            <StatRow label="Failed payments" value={data?.failed_payments_this_month || 0} icon={AlertCircle} color="amber" />
          </div>
        </Card>

        {/* Users */}
        <Card className="p-4">
          <div ref={usersRef} className="scroll-mt-4" />
          <div className="flex items-center justify-between mb-3">
            <SectionHeading icon={Users} iconClass="text-brand" className="mb-0">Users</SectionHeading>
            <span className="text-xs text-ink-muted">{users.total} total</span>
          </div>
          <div className="space-y-2">
            <div className="relative">
              <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none" />
              <Input placeholder="Search name or email" value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 h-11" data-testid="admin-user-search" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} className="h-11 text-sm" data-testid="admin-plan-filter">
                <option value="">All plans</option>
                <option value="starter">Starter (Free)</option>
                <option value="league">League</option>
                <option value="premier">Premier</option>
              </Select>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-11 text-sm" data-testid="admin-status-filter">
                <option value="">All status</option>
                <option value="active">Active</option>
                <option value="trialing">Trialing</option>
                <option value="past_due">Past due</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </div>
          </div>

          <div className={cn("mt-3 divide-y divide-black/[0.06]", usersLoading && "opacity-50")}>
            {users.users.map((u) => <UserRow key={u.id} u={u} />)}
          </div>
          {!usersLoading && users.users.length === 0 && (
            <div className="text-center py-8 text-sm text-ink-muted">No users found</div>
          )}

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-black/[0.06]">
            <Button variant="ghost" size="sm" disabled={users.page <= 1 || usersLoading} onClick={() => fetchUsers(users.page - 1)}>Prev</Button>
            <span className="text-xs text-ink-muted">Page {users.page}</span>
            <Button variant="ghost" size="sm" disabled={users.users.length < PAGE_SIZE || usersLoading} onClick={() => fetchUsers(users.page + 1)}>Next</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function SectionHeading({ icon: Icon, iconClass, children, className = "mb-3" }) {
  return (
    <h3 className={cn("font-semibold flex items-center gap-2", className)}>
      <Icon size={18} className={iconClass} />
      {children}
    </h3>
  );
}

function UserRow({ u }) {
  const plan = u.subscription?.plan_id;
  const status = u.subscription?.status || "free";
  return (
    <div className="py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-ink truncate">{u.name}</div>
          <div className="text-xs text-ink-muted truncate">{u.email}</div>
        </div>
        <div className="text-[11px] text-ink-muted shrink-0 pt-0.5">{fmtDate(u.created_at)}</div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
        <Chip tone={plan === "premier" ? "warning" : plan === "league" ? "default" : "success"} className="text-[11px]">
          {u.subscription?.plan_name || "Cricso Starter"}
        </Chip>
        <Chip tone={status === "active" ? "success" : status === "past_due" ? "warning" : status === "free" ? "default" : "danger"} className="text-[11px] capitalize">
          {status.replace("_", " ")}
        </Chip>
        <span className="text-[11px] text-ink-muted">
          {u.clubs_owned} owned • {u.clubs_member} member
        </span>
      </div>
    </div>
  );
}

function MetricCard({ title, value, icon: Icon, color, subtitle, onClick }) {
  const colors = {
    brand: "bg-brand-100 text-brand-700",
    green: "bg-green-100 text-green-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
  };
  return (
    <Card className={cn("p-4 min-w-0", onClick && "cursor-pointer btn-press")} onClick={onClick}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest text-ink-muted font-semibold truncate">{title}</div>
        <div className={cn("h-7 w-7 shrink-0 rounded-lg grid place-items-center", colors[color] || colors.brand)}>
          <Icon size={15} />
        </div>
      </div>
      <div className="text-xl font-bold text-ink mt-1 truncate tabular-nums">{value}</div>
      {subtitle && (
        <div className="text-[11px] text-ink-muted mt-0.5 flex items-center gap-0.5">
          <span className="truncate">{subtitle}</span>
          {onClick && <ChevronRight size={12} className="shrink-0" />}
        </div>
      )}
    </Card>
  );
}

function StatRow({ label, value, icon: Icon, color }) {
  const colors = {
    green: "text-green-600",
    red: "text-red-600",
    amber: "text-amber-600",
    brand: "text-brand",
  };
  return (
    <div className="flex items-center justify-between py-2.5">
      <div className="flex items-center gap-2 text-sm text-ink-muted">
        <Icon size={14} className={colors[color] || colors.brand} />
        <span>{label}</span>
      </div>
      <span className="font-bold text-ink tabular-nums">{value}</span>
    </div>
  );
}

/** ₹1.2k / ₹3.4L so a value fits under a narrow bar */
function shortInr(n) {
  if (n >= 100000) return `₹${(n / 100000).toFixed(n >= 1000000 ? 0 : 1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return `₹${Math.round(n)}`;
}

function RevenueChart({ revenue }) {
  const months = Object.keys(revenue).sort();
  const [picked, setPicked] = useState(null);
  if (months.length === 0) {
    return <div className="text-center py-8 text-sm text-ink-muted">No revenue data yet</div>;
  }

  const values = months.map((m) => revenue[m] || 0);
  const maxRevenue = Math.max(...values);
  const total = values.reduce((a, b) => a + b, 0);
  const chartHeight = 120;
  const label = (m, opts) => {
    const [year, monthNum] = m.split("-");
    return new Date(year, monthNum - 1).toLocaleDateString("en-IN", opts);
  };
  const shown = picked ?? months[months.length - 1];

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="text-xl font-bold tabular-nums truncate">{inr(revenue[shown] || 0)}</div>
          <div className="text-[11px] text-ink-muted">{label(shown, { month: "long", year: "numeric" })}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-semibold tabular-nums">{shortInr(total)}</div>
          <div className="text-[11px] text-ink-muted">12-month total</div>
        </div>
      </div>
      <div className="flex items-end gap-1" style={{ height: chartHeight + 18 }}>
        {months.map((m) => {
          const value = revenue[m] || 0;
          const height = maxRevenue > 0 ? (value / maxRevenue) * chartHeight : 0;
          return (
            <button key={m} type="button" onClick={() => setPicked(m)}
              className="flex-1 min-w-0 h-full flex flex-col justify-end items-center gap-1"
              title={`${label(m, { month: "short", year: "numeric" })}: ${inr(value)}`}>
              <div
                className={cn("w-full rounded-t transition-all duration-300", m === shown ? "bg-brand" : "bg-brand/40")}
                style={{ height: `${height}px`, minHeight: value > 0 ? "3px" : "0" }}
              />
              <span className={cn("text-[9px] leading-none", m === shown ? "text-ink font-semibold" : "text-ink-muted")}>
                {label(m, { month: "narrow" })}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
